import { AI_ASSISTANT_CONFIG } from "./ai-assistant-config";

export interface ColorScheme {
    background: string;
    foreground: string;
    card: string;
    "card-foreground": string;
    popover: string;
    "popover-foreground": string;
    primary: string;
    "primary-foreground": string;
    "primary-hover": string;
    secondary: string;
    "secondary-foreground": string;
    muted: string;
    "muted-foreground": string;
    accent: string;
    "accent-foreground": string;
    destructive: string;
    "destructive-foreground": string;
    border: string;
    input: string;
    ring: string;
    "brand-reasoning": string;
    "brand-reasoning-light": string;
    "status-warning": string;
    "status-success": string;
    "chip-basic": string;
    "chip-procedural": string;
    "chip-advanced": string;
    "chip-analytical": string;
}

export interface ThemeSchemes {
    light: ColorScheme;
    dark: ColorScheme;
}

// Default schemes defined first so they can be used in the functional part
const LIGHT_DEFAULTS: ColorScheme = {
    background: "#ffffff",
    foreground: "#18181b",
    card: "#f9fafb",
    "card-foreground": "#18181b",
    popover: "#ffffff",
    "popover-foreground": "#18181b",
    primary: "#52525b",
    "primary-foreground": "#ffffff",
    "primary-hover": "#3f3f46",
    secondary: "#f4f4f5",
    "secondary-foreground": "#18181b",
    muted: "#f4f4f5",
    "muted-foreground": "#71717a",
    accent: "#f4f4f5",
    "accent-foreground": "#18181b",
    destructive: "#dc2626",
    "destructive-foreground": "#ffffff",
    border: "#e4e4e7",
    input: "#e4e4e7",
    ring: "#52525b",
    "brand-reasoning": "#6366f1",
    "brand-reasoning-light": "#818cf8",
    "status-warning": "#f59e0b",
    "status-success": "#16a34a",
    "chip-basic": "#6366f1",
    "chip-procedural": "#8b5cf6",
    "chip-advanced": "#f59e0b",
    "chip-analytical": "#ec4899",
};

const DARK_DEFAULTS: ColorScheme = {
    background: "#09090b",
    foreground: "#fafafa",
    card: "#111113",
    "card-foreground": "#fafafa",
    popover: "#09090b",
    "popover-foreground": "#fafafa",
    primary: "#d4d4d8",
    "primary-foreground": "#18181b",
    "primary-hover": "#a1a1aa",
    secondary: "#1e1e21",
    "secondary-foreground": "#fafafa",
    muted: "#1e1e21",
    "muted-foreground": "#a1a1aa",
    accent: "#1e1e21",
    "accent-foreground": "#fafafa",
    destructive: "#7f1d1d",
    "destructive-foreground": "#fafafa",
    border: "#27272a",
    input: "#27272a",
    ring: "#a1a1aa",
    "brand-reasoning": "#6366f1",
    "brand-reasoning-light": "#818cf8",
    "status-warning": "#f59e0b",
    "status-success": "#16a34a",
    "chip-basic": "#818cf8",
    "chip-procedural": "#a78bfa",
    "chip-advanced": "#fbbf24",
    "chip-analytical": "#f472b6",
};

// Re-export defaults for compatibility
export const DEFAULT_LIGHT_COLOR_SCHEME = LIGHT_DEFAULTS;
export const DEFAULT_DARK_COLOR_SCHEME = DARK_DEFAULTS;

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}

function hexToHsl(hex: string): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }

    const hDeg = Math.round(h * 360);
    const sPercent = Math.round(s * 100);
    const lPercent = Math.round(l * 100);

    return `${hDeg} ${sPercent}% ${lPercent}%`;
}

function withOpacity(hex: string, percentage: number): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const alpha = (percentage / 100).toFixed(2);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Resolves a color value from an environment variable value or fallback.
 * Handles missing values and ensures proper hex format (adds # if missing).
 */
function resolveColor(envValue: string | undefined, fallback: string): string {
    const value = envValue || fallback;

    // Add # prefix if missing
    const hexValue = value.startsWith("#") ? value : `#${value}`;

    // Validate hex format (3 or 6 digits)
    if (
        /^#[0-9A-Fa-f]{3}$/.test(hexValue) ||
        /^#[0-9A-Fa-f]{6}$/.test(hexValue)
    ) {
        return hexValue;
    }

    return fallback.startsWith("#") ? fallback : `#${fallback}`;
}

export function getColorSchemes(): ThemeSchemes {
    const cfg = AI_ASSISTANT_CONFIG.colors;
    return {
        light: {
            background: resolveColor(
                process.env.REACT_APP_COLOR_BACKGROUND,
                cfg.light.background,
            ),
            foreground: resolveColor(
                process.env.REACT_APP_COLOR_FOREGROUND,
                cfg.light.foreground,
            ),
            card: resolveColor(
                process.env.REACT_APP_COLOR_CARD,
                cfg.light.card,
            ),
            "card-foreground": resolveColor(
                process.env.REACT_APP_COLOR_CARD_FOREGROUND,
                cfg.light.cardForeground,
            ),
            popover: resolveColor(
                process.env.REACT_APP_COLOR_POPOVER,
                cfg.light.popover,
            ),
            "popover-foreground": resolveColor(
                process.env.REACT_APP_COLOR_POPOVER_FOREGROUND,
                cfg.light.popoverForeground,
            ),
            primary: resolveColor(
                process.env.REACT_APP_COLOR_PRIMARY,
                cfg.light.primary,
            ),
            "primary-foreground": resolveColor(
                process.env.REACT_APP_COLOR_PRIMARY_FOREGROUND,
                cfg.light.primaryForeground,
            ),
            "primary-hover": resolveColor(
                process.env.REACT_APP_COLOR_PRIMARY_HOVER,
                cfg.light.primaryHover,
            ),
            secondary: resolveColor(
                process.env.REACT_APP_COLOR_SECONDARY,
                cfg.light.secondary,
            ),
            "secondary-foreground": resolveColor(
                process.env.REACT_APP_COLOR_SECONDARY_FOREGROUND,
                cfg.light.secondaryForeground,
            ),
            muted: resolveColor(
                process.env.REACT_APP_COLOR_MUTED,
                cfg.light.muted,
            ),
            "muted-foreground": resolveColor(
                process.env.REACT_APP_COLOR_MUTED_FOREGROUND,
                cfg.light.mutedForeground,
            ),
            accent: resolveColor(
                process.env.REACT_APP_COLOR_ACCENT,
                cfg.light.accent,
            ),
            "accent-foreground": resolveColor(
                process.env.REACT_APP_COLOR_ACCENT_FOREGROUND,
                cfg.light.accentForeground,
            ),
            destructive: resolveColor(
                process.env.REACT_APP_COLOR_DESTRUCTIVE,
                cfg.light.destructive,
            ),
            "destructive-foreground": resolveColor(
                process.env.REACT_APP_COLOR_DESTRUCTIVE_FOREGROUND,
                cfg.light.destructiveForeground,
            ),
            border: resolveColor(
                process.env.REACT_APP_COLOR_BORDER,
                cfg.light.border,
            ),
            input: resolveColor(
                process.env.REACT_APP_COLOR_INPUT,
                cfg.light.input,
            ),
            ring: resolveColor(
                process.env.REACT_APP_COLOR_RING,
                cfg.light.ring,
            ),
            "brand-reasoning": resolveColor(
                process.env.REACT_APP_COLOR_BRAND_REASONING,
                cfg.light.brandReasoning,
            ),
            "brand-reasoning-light": resolveColor(
                process.env.REACT_APP_COLOR_BRAND_REASONING_LIGHT,
                cfg.light.brandReasoningLight,
            ),
            "status-warning": resolveColor(
                process.env.REACT_APP_COLOR_STATUS_WARNING,
                cfg.light.statusWarning,
            ),
            "status-success": resolveColor(
                process.env.REACT_APP_COLOR_STATUS_SUCCESS,
                cfg.light.statusSuccess,
            ),
            "chip-basic": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_BASIC,
                cfg.light.chipBasic,
            ),
            "chip-procedural": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_PROCEDURAL,
                cfg.light.chipProcedural,
            ),
            "chip-advanced": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_ADVANCED,
                cfg.light.chipAdvanced,
            ),
            "chip-analytical": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_ANALYTICAL,
                cfg.light.chipAnalytical,
            ),
        },
        dark: {
            background: resolveColor(
                process.env.REACT_APP_COLOR_BACKGROUND_DARK,
                cfg.dark.background,
            ),
            foreground: resolveColor(
                process.env.REACT_APP_COLOR_FOREGROUND_DARK,
                cfg.dark.foreground,
            ),
            card: resolveColor(
                process.env.REACT_APP_COLOR_CARD_DARK,
                cfg.dark.card,
            ),
            "card-foreground": resolveColor(
                process.env.REACT_APP_COLOR_CARD_FOREGROUND_DARK,
                cfg.dark.cardForeground,
            ),
            popover: resolveColor(
                process.env.REACT_APP_COLOR_POPOVER_DARK,
                cfg.dark.popover,
            ),
            "popover-foreground": resolveColor(
                process.env.REACT_APP_COLOR_POPOVER_FOREGROUND_DARK,
                cfg.dark.popoverForeground,
            ),
            primary: resolveColor(
                process.env.REACT_APP_COLOR_PRIMARY_DARK,
                cfg.dark.primary,
            ),
            "primary-foreground": resolveColor(
                process.env.REACT_APP_COLOR_PRIMARY_FOREGROUND_DARK,
                cfg.dark.primaryForeground,
            ),
            "primary-hover": resolveColor(
                process.env.REACT_APP_COLOR_PRIMARY_HOVER_DARK,
                cfg.dark.primaryHover,
            ),
            secondary: resolveColor(
                process.env.REACT_APP_COLOR_SECONDARY_DARK,
                cfg.dark.secondary,
            ),
            "secondary-foreground": resolveColor(
                process.env.REACT_APP_COLOR_SECONDARY_FOREGROUND_DARK,
                cfg.dark.secondaryForeground,
            ),
            muted: resolveColor(
                process.env.REACT_APP_COLOR_MUTED_DARK,
                cfg.dark.muted,
            ),
            "muted-foreground": resolveColor(
                process.env.REACT_APP_COLOR_MUTED_FOREGROUND_DARK,
                cfg.dark.mutedForeground,
            ),
            accent: resolveColor(
                process.env.REACT_APP_COLOR_ACCENT_DARK,
                cfg.dark.accent,
            ),
            "accent-foreground": resolveColor(
                process.env.REACT_APP_COLOR_ACCENT_FOREGROUND_DARK,
                cfg.dark.accentForeground,
            ),
            destructive: resolveColor(
                process.env.REACT_APP_COLOR_DESTRUCTIVE_DARK,
                cfg.dark.destructive,
            ),
            "destructive-foreground": resolveColor(
                process.env.REACT_APP_COLOR_DESTRUCTIVE_FOREGROUND_DARK,
                cfg.dark.destructiveForeground,
            ),
            border: resolveColor(
                process.env.REACT_APP_COLOR_BORDER_DARK,
                cfg.dark.border,
            ),
            input: resolveColor(
                process.env.REACT_APP_COLOR_INPUT_DARK,
                cfg.dark.input,
            ),
            ring: resolveColor(
                process.env.REACT_APP_COLOR_RING_DARK,
                cfg.dark.ring,
            ),
            "brand-reasoning": resolveColor(
                process.env.REACT_APP_COLOR_BRAND_REASONING_DARK,
                cfg.dark.brandReasoning,
            ),
            "brand-reasoning-light": resolveColor(
                process.env.REACT_APP_COLOR_BRAND_REASONING_LIGHT_DARK,
                cfg.dark.brandReasoningLight,
            ),
            "status-warning": resolveColor(
                process.env.REACT_APP_COLOR_STATUS_WARNING_DARK,
                cfg.dark.statusWarning,
            ),
            "status-success": resolveColor(
                process.env.REACT_APP_COLOR_STATUS_SUCCESS_DARK,
                cfg.dark.statusSuccess,
            ),
            "chip-basic": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_BASIC_DARK,
                cfg.dark.chipBasic,
            ),
            "chip-procedural": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_PROCEDURAL_DARK,
                cfg.dark.chipProcedural,
            ),
            "chip-advanced": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_ADVANCED_DARK,
                cfg.dark.chipAdvanced,
            ),
            "chip-analytical": resolveColor(
                process.env.REACT_APP_COLOR_CHIP_ANALYTICAL_DARK,
                cfg.dark.chipAnalytical,
            ),
        },
    };
}

export function generateCssVariables(
    schemes: ThemeSchemes,
    currentTheme: "light" | "dark",
): Record<string, string> {
    const scheme = currentTheme === "light" ? schemes.light : schemes.dark;

    return {
        // Standard variables (for frontend-react Tailwind)
        "--background": scheme.background,
        "--foreground": scheme.foreground,
        "--card": scheme.card,
        "--card-foreground": scheme["card-foreground"],
        "--popover": scheme.popover,
        "--popover-foreground": scheme["popover-foreground"],
        "--primary": scheme.primary,
        "--primary-foreground": scheme["primary-foreground"],
        "--secondary": scheme.secondary,
        "--secondary-foreground": scheme["secondary-foreground"],
        "--muted": scheme.muted,
        "--muted-foreground": scheme["muted-foreground"],
        "--accent": scheme.accent,
        "--accent-foreground": scheme["accent-foreground"],
        "--destructive": scheme.destructive,
        "--destructive-foreground": scheme["destructive-foreground"],
        "--border": scheme.border,
        "--input": scheme.input,
        "--ring": scheme.ring,

        // AI-prefixed variables (for acetrials-frontend - scoped to avoid conflicts)
        "--ai-background": scheme.background,
        "--ai-foreground": scheme.foreground,
        "--ai-card": scheme.card,
        "--ai-card-foreground": scheme["card-foreground"],
        "--ai-popover": scheme.popover,
        "--ai-popover-foreground": scheme["popover-foreground"],
        "--ai-primary": scheme.primary,
        "--ai-primary-foreground": scheme["primary-foreground"],
        "--ai-primary-hover": scheme["primary-hover"],
        "--ai-secondary": scheme.secondary,
        "--ai-secondary-foreground": scheme["secondary-foreground"],
        "--ai-muted": scheme.muted,
        "--ai-muted-foreground": scheme["muted-foreground"],
        "--ai-accent": scheme.accent,
        "--ai-accent-foreground": scheme["accent-foreground"],
        "--ai-destructive": scheme.destructive,
        "--ai-destructive-foreground": scheme["destructive-foreground"],
        "--ai-border": scheme.border,
        "--ai-input": scheme.input,
        "--ai-ring": scheme.ring,

        // --color-* variables (used by Bootstrap override CSS)
        "--color-background": scheme.background,
        "--color-foreground": scheme.foreground,
        "--color-card": scheme.card,
        "--color-card-foreground": scheme["card-foreground"],
        "--color-primary": scheme.primary,
        "--color-primary-foreground": scheme["primary-foreground"],
        "--color-secondary": scheme.secondary,
        "--color-secondary-foreground": scheme["secondary-foreground"],
        "--color-muted": scheme.muted,
        "--color-muted-foreground": scheme["muted-foreground"],
        "--color-accent": scheme.accent,
        "--color-accent-foreground": scheme["accent-foreground"],
        "--color-destructive": scheme.destructive,
        "--color-destructive-foreground": scheme["destructive-foreground"],
        "--color-border": scheme.border,
        "--color-input": scheme.input,
        "--color-ring": scheme.ring,
        "--color-brand-reasoning": scheme["brand-reasoning"],
        "--color-brand-reasoning-light": scheme["brand-reasoning-light"],
        "--color-status-warning": scheme["status-warning"],
        "--color-status-success": scheme["status-success"],
        "--color-chip-basic": scheme["chip-basic"],
        "--color-chip-procedural": scheme["chip-procedural"],
        "--color-chip-advanced": scheme["chip-advanced"],
        "--color-chip-analytical": scheme["chip-analytical"],

        "--color-primary-5": withOpacity(scheme.primary, 5),
        "--color-primary-10": withOpacity(scheme.primary, 10),
        "--color-primary-20": withOpacity(scheme.primary, 20),
        "--color-primary-30": withOpacity(scheme.primary, 30),
        "--color-primary-40": withOpacity(scheme.primary, 40),
        "--color-primary-50": withOpacity(scheme.primary, 50),

        "--color-brand-reasoning-5": withOpacity(scheme["brand-reasoning"], 5),
        "--color-brand-reasoning-10": withOpacity(
            scheme["brand-reasoning"],
            10,
        ),
        "--color-brand-reasoning-20": withOpacity(
            scheme["brand-reasoning"],
            20,
        ),
        "--color-brand-reasoning-30": withOpacity(
            scheme["brand-reasoning"],
            30,
        ),
        "--color-brand-reasoning-40": withOpacity(
            scheme["brand-reasoning"],
            40,
        ),
        "--color-brand-reasoning-50": withOpacity(
            scheme["brand-reasoning"],
            50,
        ),
        "--color-brand-reasoning-80": withOpacity(
            scheme["brand-reasoning"],
            80,
        ),

        "--color-status-warning-5": withOpacity(scheme["status-warning"], 5),
        "--color-status-warning-10": withOpacity(scheme["status-warning"], 10),
        "--color-status-warning-20": withOpacity(scheme["status-warning"], 20),
        "--color-status-warning-50": withOpacity(scheme["status-warning"], 50),
        "--color-status-warning-80": withOpacity(scheme["status-warning"], 80),

        "--color-status-success-5": withOpacity(scheme["status-success"], 5),
        "--color-status-success-10": withOpacity(scheme["status-success"], 10),
        "--color-status-success-20": withOpacity(scheme["status-success"], 20),

        "--color-chip-basic-5": withOpacity(scheme["chip-basic"], 5),
        "--color-chip-basic-10": withOpacity(scheme["chip-basic"], 10),
        "--color-chip-basic-20": withOpacity(scheme["chip-basic"], 20),

        "--color-chip-procedural-5": withOpacity(scheme["chip-procedural"], 5),
        "--color-chip-procedural-10": withOpacity(
            scheme["chip-procedural"],
            10,
        ),
        "--color-chip-procedural-20": withOpacity(
            scheme["chip-procedural"],
            20,
        ),

        "--color-chip-advanced-5": withOpacity(scheme["chip-advanced"], 5),
        "--color-chip-advanced-10": withOpacity(scheme["chip-advanced"], 10),
        "--color-chip-advanced-20": withOpacity(scheme["chip-advanced"], 20),

        "--color-chip-analytical-5": withOpacity(scheme["chip-analytical"], 5),
        "--color-chip-analytical-10": withOpacity(
            scheme["chip-analytical"],
            10,
        ),
        "--color-chip-analytical-20": withOpacity(
            scheme["chip-analytical"],
            20,
        ),
    };
}

export function applyCssVariablesToRoot(
    schemes: ThemeSchemes,
    theme: "light" | "dark",
): void {
    if (typeof window === "undefined") return;

    const root = document.documentElement;
    const variables = generateCssVariables(schemes, theme);

    Object.entries(variables).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
}

export function getBrandReasoningColor(opacity?: number): string {
    if (opacity !== undefined) {
        return `rgba(99, 102, 241, ${opacity})`;
    }
    return "#6366f1";
}

export function getStatusWarningColor(opacity?: number): string {
    if (opacity !== undefined) {
        return `rgba(245, 158, 11, ${opacity})`;
    }
    return "#f59e0b";
}

export function getStatusSuccessColor(opacity?: number): string {
    if (opacity !== undefined) {
        return `rgba(22, 163, 74, ${opacity})`;
    }
    return "#16a34a";
}
